<template>
  <div class="w-full h-full flex flex-col gap-3 px-4 pb-4">
    <!-- Filters: status as a light underline tab bar, branch as a compact dropdown -->
    <div class="flex items-center gap-3 border-b border-gray-100 flex-none">
      <div class="status-tabs flex gap-5 overflow-x-auto flex-1 min-w-0">
        <button
          v-for="filter of statusFilters"
          :key="filter.value"
          type="button"
          class="relative flex items-center gap-1.5 whitespace-nowrap flex-none pt-1 pb-2.5 text-[13px] transition-colors"
          :class="selectedStatus === filter.value ? 'text-primary font-semibold' : 'text-gray-500 hover:text-gray-700'"
          @click="selectedStatus = filter.value"
        >
          <span>{{ filter.label }}</span>
          <span
            v-if="filter.value === 'open' && openCount > 0"
            class="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold leading-none"
            :class="selectedStatus === filter.value ? 'bg-primary text-white' : 'bg-red-500 text-white'"
          >{{ openCount }}</span>
          <span v-if="selectedStatus === filter.value" class="absolute inset-x-0 -bottom-px h-[2px] bg-primary rounded-full" />
        </button>
      </div>

<!--      <Select-->
<!--        v-if="adminStore.hasBranches"-->
<!--        v-model="selectedBranchId"-->
<!--        :options="branchSelectOptions"-->
<!--        optionLabel="label"-->
<!--        optionValue="value"-->
<!--        size="small"-->
<!--        class="branch-select flex-none mb-1.5 w-48"-->
<!--      />-->
    </div>

    <div v-if="loading" class="w-full flex justify-center py-10">
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
    </div>

    <div v-else-if="filteredOrders.length === 0"
         class="w-full flex flex-col justify-center items-center py-12 gap-2 text-center">
      <i class="pi pi-shopping-bag text-4xl text-gray-300" />
      <span class="font-medium">אין הזמנות להצגה</span>
      <span class="text-sm text-gray-500 px-6">כשלקוחות יזמינו מוצרים מהאתר, ההזמנות יופיעו כאן</span>
    </div>

    <div v-else class="w-full flex flex-col gap-3 overflow-y-auto">
      <div
        v-for="order of filteredOrders"
        :key="order._id"
        class="w-full border border-gray-200 rounded-2xl p-4 flex flex-col gap-2 bg-white cursor-pointer hover:border-gray-300"
        @click="openOrderDetails(order)"
      >
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2">
            <span class="font-medium">הזמנה #{{ order.orderNumber }}</span>
            <Tag :severity="statusSeverity(order.status)" :value="statusLabel(order.status)" />
          </div>
          <span class="font-medium">₪{{ order.totalAmount }}</span>
        </div>
        <div class="flex justify-between items-center text-sm text-gray-600">
          <span>{{ order.customerName }}</span>
          <span>{{ formatDate(order.createdAt) }}</span>
        </div>
        <span class="text-sm text-gray-500 truncate">{{ itemsSummary(order) }}</span>
        <span v-if="adminStore.hasBranches" class="text-xs text-gray-400">
          <i class="pi pi-map-marker text-xs" /> {{ branchName(order.branchId) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { DateTime } from 'luxon';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import ProgressSpinner from 'primevue/progressspinner';
import { IProductOrder, MAIN_BRANCH_ID, ProductOrderStatusEnum } from '@torim-app/types';
import { useDrawer } from '@torim-app/shared-components';
import { useAdminStore } from '@app/stores';
import { ProductOrdersApi } from '@app/api/admin/product-orders-api';
import ProductOrderDetails from './ProductOrderDetails.vue';
import ManualSaleDrawer from '@app/components/admin/modals/ManualSaleDrawer.vue';
import { trackEvent } from '@app/composables/posthog';

const adminStore = useAdminStore();
const { openDrawer, closeDrawer } = useDrawer();

const orders = ref<IProductOrder[]>([]);
const loading = ref(false);
const selectedStatus = ref<string>('open');
const selectedBranchId = ref<string>('');

const emit = defineEmits<{ (e: 'openCountChanged', count: number): void }>();

const statusFilters = [
  { value: 'open', label: 'פתוחות' },
  { value: ProductOrderStatusEnum.Received, label: 'חדשות' },
  { value: ProductOrderStatusEnum.Ready, label: 'מוכנות לאיסוף' },
  { value: ProductOrderStatusEnum.PickedUp, label: 'נאספו' },
  { value: 'all', label: 'הכל' }
];

const branchOptions = computed(() => adminStore.getBranchSelectOptions);
const branchSelectOptions = computed(() => [
  { label: 'כל הסניפים', value: '' },
  ...branchOptions.value.map(b => ({ label: b.name, value: b.id }))
]);

const openCount = computed(() =>
  orders.value.filter(o => [ProductOrderStatusEnum.Received, ProductOrderStatusEnum.Ready].includes(o.status)).length);

const filteredOrders = computed(() => {
  let list = orders.value;
  if (selectedStatus.value === 'open')
    list = list.filter(o => [ProductOrderStatusEnum.Received, ProductOrderStatusEnum.Ready].includes(o.status));
  else if (selectedStatus.value !== 'all')
    list = list.filter(o => o.status === selectedStatus.value);

  if (selectedBranchId.value)
    list = list.filter(o => (o.branchId ?? MAIN_BRANCH_ID) === selectedBranchId.value);

  return list;
});

const statusLabel = (status: ProductOrderStatusEnum) => {
  switch (status) {
    case ProductOrderStatusEnum.Received: return 'חדשה';
    case ProductOrderStatusEnum.Ready: return 'מוכנה לאיסוף';
    case ProductOrderStatusEnum.PickedUp: return 'נאספה';
    case ProductOrderStatusEnum.Cancelled: return 'בוטלה';
    default: return status;
  }
};

const statusSeverity = (status: ProductOrderStatusEnum) => {
  switch (status) {
    case ProductOrderStatusEnum.Received: return 'info';
    case ProductOrderStatusEnum.Ready: return 'warn';
    case ProductOrderStatusEnum.PickedUp: return 'success';
    case ProductOrderStatusEnum.Cancelled: return 'danger';
    default: return 'secondary';
  }
};

const itemsSummary = (order: IProductOrder) =>
  order.items.map(item => `${item.name} x${item.amount}`).join(', ');

const branchName = (branchId?: string) =>
  branchOptions.value.find(b => b.id === (branchId ?? MAIN_BRANCH_ID))?.name ?? '';

const formatDate = (date?: Date | string) =>
  date ? DateTime.fromISO(String(date)).setLocale('he').toLocaleString(DateTime.DATETIME_MED) : '';

const fetchOrders = async () => {
  loading.value = true;
  try {
    orders.value = await ProductOrdersApi.getOrders();
    emit('openCountChanged', openCount.value);
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const openManualSale = () => {
  trackEvent('manual_sale_opened');
  openDrawer(ManualSaleDrawer, {
    onCreated: async () => {
      await fetchOrders();
    },
    onClose: closeDrawer
  }, {
    title: 'מכירה ידנית'
  });
};

const openOrderDetails = (order: IProductOrder) => {
  trackEvent('product_order_opened', { order_status: order.status });
  openDrawer(ProductOrderDetails, {
    order,
    onUpdated: async () => {
      await fetchOrders();
    },
    onClose: closeDrawer
  }, {
    title: `הזמנה #${order.orderNumber}`
  });
};

onMounted(fetchOrders);

// Opened from the page header button (see ShopManagement) so there's a single
// "add" affordance per tab instead of a separate in-list button.
defineExpose({ refresh: fetchOrders, openManualSale });
</script>

<style scoped lang="scss">
.status-tabs {
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}
</style>
